/** @jsxImportSource @emotion/react */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@mui/material';
import _ from 'lodash';
import {
  SearchBox,
  SearchRows,
  SearchCols,
  SearchBoxRow,
  InputBox,
} from 'components/layouts/SearchBox';
import { UseToggle } from 'components/inputs/CustomInput';
import CustomInputWithSearch from 'components/inputs/CustomInputWithSearch';
import { SubTitleLayout, SubTitleGroup, ControlBtnGroup } from 'components/layouts/ContentLayout';
import { IconButton } from 'components/buttons/IconSVG';
import { ParaItemCondition } from 'models/ip/popup/ParaItem';
import { findParaItem } from 'apis/ip/popup/ParaItem';
import { useMessageBar } from 'hooks/useMessageBar';
import CustomGrid from 'components/grids/CustomGrid';
import CustomDialog from 'components/modals/common/CustomDialog';
import SearchBoxButtons from 'components/buttons/SearchBoxButtons';

type Props = {
  open: boolean;
  close: () => void;
  condition?: {
    paraItem?: string;
    paraItemNm?: string;
  };
  onCallback: (item: ParaItemCondition | ParaItemCondition[]) => void;
  singleSelect?: boolean;
};

export const ParaItemPopUp = ({
  open,
  close,
  condition,
  onCallback,
  singleSelect = true,
}: Props) => {
  const { t } = useTranslation();
  const { openMessageBar } = useMessageBar();
  const gridRef = useRef<any>();

  const [searchParaItemData, setSearchParaItemData] = useState<ParaItemCondition>({
    paraItem: condition?.paraItem || '',
    paraItemNm: condition?.paraItemNm || '',
    useYn: 'Y',
  });
  const [rowData, setRowData] = useState<ParaItemCondition[]>([]);
  const [colWidthMap, setColWidthMap] = useState<any>({
    qselect: 120,
    paraItem: 286,
    useYn: 276,
    paraItemNm: '*',
  });

  useEffect(() => {
    searchBtn(searchParaItemData);
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    setSearchParaItemData({ ...searchParaItemData, [e.target.name]: e.target.value });
  };

  const searchBtn = async (condition: ParaItemCondition) => {
    await findParaItem(condition).then((response) => {
      if (response) {
        const rowData = response || [];
        setRowData(rowData);

        if (Object.keys(rowData).length === 0) {
          openMessageBar({
            type: 'error',
            content: t(
              'com.label.조회 가능한 데이터가 없습니다.',
              '조회 가능한 데이터가 없습니다.'
            ),
          });
          return;
        }
      }
    });
  };

  const handleConfirm = () => {
    const items = gridRef.current.rows.filter((r) => r.isSelected).map((o) => o.dataItem);
    if ((items || []).length > 0) {
      onCallback(items[0]);
    }
    close();
  };

  const handleButtonClick = (params) => {
    // data에 현재 행의 데이터가 포함되어 있음123345
    onCallback(!_.isNull(params.data) ? params.data : null);
    close();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      searchBtn(searchParaItemData);
    }
  };

  const layoutDefinition = [
    {
      binding: 'btnSelect',
      header: ' ',
      cssClass: 'WijmoRelay',
      cellTemplate: '<Button />',
      width: 39,
    },
    {
      binding: 'paraItem',
      header: String(t('ip.grid.파라미터 항목코드', '파라미터 항목코드')),
      align: 'center',
      width: colWidthMap.paraItem,
    },
    {
      binding: 'paraItemNm',
      header: String(t('ip.grid.파라미터 항목명', '파라미터 항목명')),
      align: 'center',
      width: colWidthMap.paraItemNm,
      // flex: 1,
    },
    {
      binding: 'useYn',
      header: String(t('com.label.사용여부', '사용여부')),
      align: 'center',
      width: colWidthMap.useYn,
    },
  ];

  const onInitialized = (grid) => {
    gridRef.current = grid;

    //columnGroups속성 사용할 경우 -> Column 너비 수동 지정
    grid.resizingColumn.addHandler((s, e) => {
      const col = e.panel.columns[e.col];
      colWidthMap[col.binding] = col.width;
      setColWidthMap(colWidthMap);
    });

    grid.hostElement.addEventListener('dblclick', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const item = grid.rows[ht.row].dataItem;

      if (item) {
        handleButtonClick({ data: item });
      }
    });

    grid.hostElement.addEventListener('click', (e) => {
      if (grid.rows.length == 0) return;

      const ht = grid.hitTest(e);
      if (ht.row < 0 || ht.col < 0) return;

      const binding = grid.columns[ht.col].binding;
      const item = grid.rows[ht.row].dataItem;

      if (ht.panel === grid.cells) {
        // Quick Select
        if ('btnSelect' === binding) {
          handleButtonClick({ data: item });
        }
      }
    });
  };

  const dialogButtons = [
    <Button
      key="confirm"
      css={IconButton.button}
      className="confirm"
      onClick={handleConfirm}
      disableRipple
    >
      {t('com.button.선택', '선택')}
    </Button>,
  ];
  return (
    <>
      <CustomDialog
        open={open}
        onClose={close}
        onCancel={close}
        size="lg"
        title={t('ip.label.파라미터 항목', '파라미터 항목')}
        buttons={dialogButtons}
      >
        <SearchBox>
          <SearchBoxRow>
            <InputBox>
              <SearchRows>
                <SearchCols>
                  <label>{t('ip.label.파라미터 항목코드', '파라미터 항목코드')}</label>
                  <CustomInputWithSearch
                    name="paraItem"
                    placeholder={String(
                      t(
                        'ip.msg.파라미터 항목코드를 입력해 주세요.',
                        '파라미터 항목코드를 입력해 주세요.'
                      )
                    )}
                    value={searchParaItemData.paraItem}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </SearchCols>
                <SearchCols>
                  <label>{t('ip.label.파라미터 항목명', '파라미터 항목명')}</label>
                  <CustomInputWithSearch
                    name="paraItemNm"
                    placeholder={String(
                      t(
                        'ip.msg.파라미터 항목명을 입력해 주세요.',
                        '파라미터 항목명을 입력해 주세요.'
                      )
                    )}
                    value={searchParaItemData.paraItemNm}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                </SearchCols>
                <SearchCols>
                  <label>{t('com.label.사용여부', '사용여부')}</label>
                  <UseToggle className="switch">
                    <input
                      type="checkbox"
                      name="useYn"
                      value={searchParaItemData.useYn}
                      checked={searchParaItemData.useYn === 'Y'}
                      onChange={() => {
                        setSearchParaItemData({
                          ...searchParaItemData,
                          useYn: searchParaItemData.useYn === 'Y' ? 'N' : 'Y',
                        });
                      }}
                    />
                    <span className="slider"></span>
                    <span
                      className="labels"
                      data-on={t('com.label.사용', '사용')}
                      data-off={t('com.label.미사용', '미사용')}
                    ></span>
                  </UseToggle>
                </SearchCols>
              </SearchRows>
            </InputBox>
            <SearchBoxButtons
              defaultCondition={{ useYn: 'Y' }}
              setReset={setSearchParaItemData}
              onSearch={() => searchBtn(searchParaItemData)}
            />
          </SearchBoxRow>
        </SearchBox>
        <SubTitleLayout>
          <SubTitleGroup>
            <h3>{t('ip.label.파라미터 항목', '파라미터 항목')}</h3>
            <span className="total">
              {t('com.label.총', '총')} <span>{(rowData || []).length.toLocaleString()}</span>
              {t('com.label.건', '건')}
            </span>
          </SubTitleGroup>
          <ControlBtnGroup>
            <Button
              css={IconButton.button}
              className="refresh"
              onClick={() => searchBtn(searchParaItemData)}
              disableRipple
            >
              {t('com.button.새로고침', '새로고침')}
            </Button>
          </ControlBtnGroup>
        </SubTitleLayout>

        <CustomGrid
          layoutDefinition={layoutDefinition}
          rowData={rowData}
          height={260}
          rowSelection={'single'}
          allowPinning={false}
          autoCheck={true}
          isReadOnly={true}
          initialized={onInitialized}
          excludeFilter={['btnSelect']}
        />
      </CustomDialog>
    </>
  );
};
